import { ItemProvenance, Status1 } from '../types';

/**
 * Provenance helpers for status/comment/result items.
 * See docs/roadmap/add-mcp/SPEC.Architecture.md §5.3-§5.4.
 *
 * Reusable by any item renderer (statuses, comments, gokrComments) that has
 * a `createdBy` / `lastUpdatedBy` userName and an optional `via`.
 */

/** Returns the client display name, or null when the item was not written via a non-web client. */
export function getViaClientName(via: ItemProvenance | undefined | null): string | null {
  if (!via) return null;
  const name = via.clientName?.trim();
  if (name) return name;
  return via.clientId ? 'an AI agent' : null;
}

/** "userName via clientName" when via is set, otherwise just userName. */
export function formatAuthorWithVia(
  userName: string | undefined | null,
  via: ItemProvenance | undefined | null,
): string {
  const name = userName || 'Unknown User';
  const client = getViaClientName(via);
  return client ? `${name} via ${client}` : name;
}

/**
 * "Last set by userName (via clientName)" for status tooltips.
 * Returns null when no last writer is known (e.g. legacy statuses without lastUpdatedBy).
 */
export function formatLastSetBy(
  userName: string | undefined | null,
  via: ItemProvenance | undefined | null,
): string | null {
  if (!userName) return null;
  const client = getViaClientName(via);
  return client ? `Last set by ${userName} (via ${client})` : `Last set by ${userName}`;
}

/**
 * Builds the status to write from a web edit.
 *
 * Field ownership rule:
 * - EDIT PAYLOAD (owned by the write, never carried over from the existing item):
 *   `statusCode` and `value`. A write that does not supply `value` (e.g. discrete 'p'
 *   on a metric cell) clears it, so a stale number is not shown on the new status.
 * - PROVENANCE (reset by the write): `lastUpdatedDate`, `lastUpdatedBy` (set to the web
 *   user, or removed when the user is unknown rather than keeping a previous agent's
 *   name), and `via` (removed: the web app is now the last writer).
 * - METADATA (preserved from the existing item): `uuid`, `shortId`, `createdDate`,
 *   `createdBy`, `knownConflicts`, `resolvedConflicts`, and any unknown fields written
 *   by other clients.
 *
 * Keys are deleted rather than set to undefined because Firestore rejects undefined.
 */
export function buildWebStatusWrite(
  existing: Status1 | undefined,
  fields: Omit<Status1, 'createdDate'> & { createdDate?: string },
  userName: string | undefined,
  now: string,
): Status1 {
  const merged: Status1 = {
    ...(existing || {}),
    ...fields,
    createdDate: existing?.createdDate || fields.createdDate || now,
    lastUpdatedDate: now,
  } as Status1;
  if (fields.value === undefined) delete merged.value;
  if (userName) {
    merged.lastUpdatedBy = userName;
    if (!existing) merged.createdBy = userName;
  } else {
    delete merged.lastUpdatedBy;
  }
  delete merged.via;
  return merged;
}
